import React, { createContext, useContext, useState, useEffect } from 'react';
import storage from '../utils/storage';
import apiClient, { User } from '../shared/api/apiClient';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = storage.getItem('nashezoloto_token');
      
      if (token) {
        try {
          // Verify token by fetching current user
          const userData = await apiClient.getCurrentUser();
          setUser(userData);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Token validation failed:', error);
          // Clear invalid token
          storage.removeItem('nashezoloto_token');
          storage.removeItem('nashezoloto_user');
        }
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Login via API
      const loginResponse = await apiClient.login({
        username: email,
        password: password,
      });

      // Store token
      storage.setItem('nashezoloto_token', loginResponse.access_token);

      // Fetch user data
      const userData = await apiClient.getCurrentUser();
      
      // Store user data
      storage.setItem('nashezoloto_user', JSON.stringify(userData));
      
      setUser(userData);
      setIsAuthenticated(true);
      
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      setError(error instanceof Error ? error.message : 'Login failed');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    storage.removeItem('nashezoloto_token');
    storage.removeItem('nashezoloto_user');
    
    // Clear chat data from localStorage
    try {
      // Clear all chat messages
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('nashezoloto_chat_')) {
          localStorage.removeItem(key);
        }
      });
      // Clear chat list
      localStorage.removeItem('nashezoloto_chats');
    } catch (error) {
      console.error('Error clearing chat data:', error);
    }
    
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      login, 
      logout, 
      loading, 
      error 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
